<template>
  <ul
    v-if="facts.length"
    class="kb-subkrewe-facts"
  >
    <li
      v-for="fact in facts"
      :key="fact"
    >
      {{ fact }}
    </li>
  </ul>
</template>

<script setup lang="ts">
import type { SubKrewe } from '~/shared/types/subkrewe'
import { formatDate } from '~/utils/dateUtils'
import { admissionShortLabel, musicShortLabel } from '~/utils/subkreweDisplay'

// At-a-glance facts that used to be columns in the admin SubKrewes table.
const props = defineProps<{
  subkrewe: SubKrewe
  /** Everyone in it now: members, captains, red shirts and children */
  memberCount?: number
}>()

// Firestore hands back Timestamps where the type says Date, and a pending
// serverTimestamp() until the write lands; only format what is a real date.
type DateLike = Date | { toDate: () => Date }
const isDateLike = (value: unknown): value is DateLike =>
  value instanceof Date || typeof (value as { toDate?: unknown } | null)?.toDate === 'function'

const facts = computed(() => {
  const sk = props.subkrewe
  const list: string[] = []
  if (isDateLike(sk.created_at)) list.push(`Created ${formatDate(sk.created_at)}`)
  if (typeof props.memberCount === 'number') list.push(`${props.memberCount} ${props.memberCount === 1 ? 'member' : 'members'}`)
  if (sk.estimated_members) list.push(`${sk.estimated_members} expected at registration`)
  const music = musicShortLabel(sk.music)
  list.push(music ? `Music: ${music.toLowerCase()}` : 'No music')
  const admission = admissionShortLabel(sk.admission_policy)
  if (admission) list.push(`Admission: ${admission.toLowerCase()}`)
  return list
})
</script>

<style scoped>
/* Inset to line up with the section titles below (1rem padding + 1px border);
   the spacing below comes from the drawer (mb-*), so no margin here */
.kb-subkrewe-facts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0;
  padding: 0 calc(1rem + 1px);
  list-style: none;
  font: 12px/1.5 var(--kb-font-mono);
  color: var(--kb-muted);
}

/* The dot ends each fact, so a wrapped line never starts with one */
.kb-subkrewe-facts li:not(:last-child)::after {
  content: '·';
  margin: 0 0.5rem;
  color: var(--kb-dim);
}
</style>
